import React, { useState, useEffect } from "react"

import { Toggle } from "../../../../components/shared/Inputs"
import QuillEditor from "../../../../components/shared/QuillEditor"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"
import BackgroundColorToggle from "../shared/BackgroundColorToggle"
import ConfirmSaveModal from "../shared/ConfirmSaveModal"

import EditorFooter from "./shared/EditorFooter"

const ContactEditor = ({ section, practice, onSave, onCancel, onDelete, isEditing = false }) => {
  const { handleUpdateSectionPreview, clearPreview } = useWebsiteBuilderV2()
  const [formData, setFormData] = useState({
    headline: section?.headline || "Contact",
    body: section?.body || "",
    settings: section?.settings || {
      showMap: true,
      showBackgroundColor: false,
      backgroundColor: "#ffffff",
      // keep existing contact options for backwards compatibility (not exposed in this editor UI)
      showContactForm: true,
      showPhoneNumber: true,
      showEmail: true,
      showAddress: true,
      showBusinessHours: true,
      showSocialLinks: false,
      formButtonText: "Send Message",
      phoneNumber: practice?.phoneNumber || "",
      email: practice?.email || "",
      address: practice?.fullAddress || "",
      businessHours: section?.settings?.businessHours || practice?.businessHours || ""
    }
  })

  const [errors, setErrors] = useState({})
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  useEffect(() => {
    if (section) {
      setFormData({
        headline: section.headline || "Contact",
        body: section.body || "",
        settings: {
          showMap: true,
          showBackgroundColor: false,
          backgroundColor: "#ffffff",
          showContactForm: true,
          showPhoneNumber: true,
          showEmail: true,
          showAddress: true,
          showBusinessHours: true,
          showSocialLinks: false,
          formButtonText: "Send Message",
          phoneNumber: practice?.phoneNumber || "",
          email: practice?.email || "",
          address: practice?.fullAddress || "",
          businessHours: section.settings?.businessHours || practice?.businessHours || "",
          ...section.settings
        }
      })
    }
  }, [section, practice])

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }))
    }

    if (section?.id && ["headline", "body"].includes(field)) {
      handleUpdateSectionPreview(section.id, {
        ...section,
        ...formData,
        [field]: value
      })
    }
  }

  const handleSettingsChange = (key, value) => {
    setFormData((prev) => {
      const newSettings = { ...prev.settings, [key]: value }

      // Immediately update preview for important settings (avoids useEffect loops)
      const importantSettings = [
        "showMap",
        "showBackgroundColor",
        "backgroundColor",
        "showContactForm",
        "formButtonText"
      ]
      if (section?.id && importantSettings.includes(key)) {
        handleUpdateSectionPreview(section.id, {
          ...section,
          ...prev,
          settings: newSettings
        })
      }

      return {
        ...prev,
        settings: newSettings
      }
    })
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.headline.trim()) newErrors.headline = "Headline is required"
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e?.preventDefault?.()
    if (validateForm()) {
      setShowConfirmModal(true)
    }
  }

  const handleConfirmSave = () => {
    setShowConfirmModal(false)
    onSave({
      layout: "contact",
      ...formData
    })
  }

  const handleCancelClick = () => {
    clearPreview()
    onCancel()
  }

  return (
    <>
      <div className="box-border flex flex-col items-start justify-start gap-6 px-8 pb-[120px] pt-6">
        {/* Title */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Title</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">
                Create a powerful headline to capture attention and communicate your core message.
              </p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative w-full shrink-0 rounded-lg bg-white">
              <input
                type="text"
                value={formData.headline}
                onChange={(e) => handleChange("headline", e.target.value)}
                placeholder="Contact"
                className="relative box-border flex w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
                required
              />
            </div>
            {errors.headline && (
              <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-red-500">
                <p className="block leading-[19.5px]">{errors.headline}</p>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Description</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Add a detailed description to provide more context.</p>
            </div>
          </div>
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <QuillEditor value={formData.body} onChange={(html) => handleChange("body", html)} />
          </div>
        </div>

        {/* Location map */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Location map</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Show the approximate location of your business on a map.</p>
            </div>
          </div>
          <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-3 p-0">
            <Toggle
              checked={!!formData.settings.showMap}
              onChange={(checked) => handleSettingsChange("showMap", checked)}
            />
            <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
              <p className="block whitespace-pre leading-[24px]">Show map</p>
            </div>
          </div>
        </div>

        {/* Business Hours */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Business Hours</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">
                Enter your business hours (e.g., &quot;Monday - Friday: 9AM - 5PM&quot;).
                {(() => {
                  const hasPracticeHours = practice?.businessHours && practice.businessHours.trim() !== ""
                  const hasCustomHours =
                    formData.settings.businessHours && formData.settings.businessHours.trim() !== ""
                  const shouldShowIndicator = hasPracticeHours && !hasCustomHours

                  return (
                    shouldShowIndicator && (
                      <span className="text-[#0d9da4]">
                        {" "}
                        Currently using practice hours: &quot;{practice.businessHours}&quot;
                      </span>
                    )
                  )
                })()}
              </p>
            </div>
          </div>
          <div className="relative w-full shrink-0 rounded-lg bg-white">
            <div className="relative flex w-full">
              <input
                type="text"
                value={formData.settings.businessHours}
                onChange={(e) => handleSettingsChange("businessHours", e.target.value)}
                placeholder={practice?.businessHours || "Monday - Friday: 9AM - 5PM"}
                className="relative box-border flex w-full flex-row content-stretch items-center justify-between text-clip rounded-lg border border-[#c8d6df] px-3 py-2 font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-[#0b3954] focus:border-[#0d9da4] focus:outline-none"
              />
              {(() => {
                const hasPracticeHours = practice?.businessHours && practice.businessHours.trim() !== ""
                const hasCustomHours = formData.settings.businessHours && formData.settings.businessHours.trim() !== ""
                const shouldShowButton = hasPracticeHours && !hasCustomHours

                return (
                  shouldShowButton && (
                    <button
                      type="button"
                      onClick={() => handleSettingsChange("businessHours", practice.businessHours)}
                      className="ml-2 rounded border border-[#0d9da4] bg-[#0d9da4] px-3 py-2 text-xs font-medium text-white hover:bg-[#0b8a90]">
                      Use Practice Hours
                    </button>
                  )
                )
              })()}
            </div>
          </div>
        </div>

        {/* Background Color Section */}
        <BackgroundColorToggle formData={formData} onSettingsChange={handleSettingsChange} sectionType="contact" />
      </div>

      {/* Footer Buttons */}
      <EditorFooter onDelete={onDelete} onCancel={handleCancelClick} onSave={handleSubmit} showDelete={isEditing} />

      {/* Confirm Save Modal */}
      <ConfirmSaveModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSave}
      />
    </>
  )
}

export default ContactEditor
